/**
 * The second thing the status button of an order says: whether a driver has
 * the order while it is still at the restaurant.
 *
 * WHY. In the driver flow of api#145 (on in production), a driver's «قبول» and
 * an admin's assignment keep the status. So «قيد التحضير» covered two states:
 * nobody has the order, and a driver is on his way to collect it. The button
 * shows the second one in another colour.
 *
 * After the pickup the status itself changes to `in the way`, so no other
 * status needs a second colour.
 *
 * THE MARK IS A SEPARATE FACT: a driver has this order now. It shows on every
 * running order that has a driver, also after the pickup. It showed only
 * before the pickup, so «في الطريق» with a driver had no mark, and it looked
 * the same as «في الطريق» that an admin set with no driver. An order on its
 * way with no mark is now an order that nobody carries.
 */

/** The statuses in which the order is still at the restaurant. */
const AT_RESTAURANT_STATUSES = Object.freeze(["not started", "preparing", "finished"]);

/** The statuses in which the order is on its way to the customer. `picked up` is an old one. */
const ON_THE_WAY_STATUSES = Object.freeze(["in the way", "picked up"]);

/**
 * @param {{status?: string, deliveryId?: number|null}|null|undefined} order
 * @returns {boolean} true when the order is at the restaurant and has a driver:
 *   the button has its second colour
 */
export const hasDriverAtRestaurant = (order) =>
    AT_RESTAURANT_STATUSES.includes(order?.status) && Boolean(order?.deliveryId);

/**
 * @param {{status?: string, deliveryId?: number|null}|null|undefined} order
 * @returns {boolean} true when the order is running and a driver has it: the
 *   button has the driver mark. An order at its end (complete, rejected) has none.
 */
export const hasDriverMark = (order) =>
    (AT_RESTAURANT_STATUSES.includes(order?.status) ||
        ON_THE_WAY_STATUSES.includes(order?.status)) &&
    Boolean(order?.deliveryId);
